/**
 * OffersCompareGrid
 *
 * The hero side-by-side comparison surface for the Offers page. Layout:
 * a fixed 220px label column followed by N flex offer columns. Winner column
 * gets a mint linear-gradient background + "Astor pick" GlassChip badge in
 * the top-right corner. Each column footer carries three actions:
 *   - Accept (glow if winner, contained otherwise)
 *   - Counter (outlined)
 *   - Ask Astor (ghost + chat icon)
 *
 * Row sequence is driven by `COMPARE_ROWS`; section dividers carry a `divider`
 * flag and bold label, value rows defer cell rendering to `OfferCellValue`.
 */

import ChatBubbleOutlinedIcon from '@mui/icons-material/ChatBubbleOutlined';
import {
  alpha,
  Box,
  GlassButton,
  GlassChip,
  type Theme,
  Typography,
  useI18n,
  useTheme,
} from '@ops-matrix/design-system-ui';
import { type CompareRowKey, OfferCellValue } from './OfferCellValue';
import type { OfferRecord } from './types';

interface CompareRow {
  key: CompareRowKey | string;
  labelKey: string;
  /** When true, render as a thin uppercase section divider (no per-offer cell). */
  divider?: boolean;
}

const COMPARE_ROWS: CompareRow[] = [
  { key: 'lender', labelKey: 'consumer.offers.compare.lender' },
  { key: 'amount', labelKey: 'consumer.offers.compare.amount' },
  { key: 'rate', labelKey: 'consumer.offers.compare.rate' },
  { key: 'term', labelKey: 'consumer.offers.compare.term' },
  { key: 'monthly', labelKey: 'consumer.offers.compare.monthly' },
  { key: 'section-pros', labelKey: 'consumer.offers.compare.sectionPros', divider: true },
  { key: 'highlights', labelKey: 'consumer.offers.compare.highlights' },
  { key: 'cautions', labelKey: 'consumer.offers.compare.cautions' },
  { key: 'section-costs', labelKey: 'consumer.offers.compare.sectionCosts', divider: true },
  { key: 'origination', labelKey: 'consumer.offers.compare.origination' },
  { key: 'fees', labelKey: 'consumer.offers.compare.totalFees' },
  { key: 'section-fit', labelKey: 'consumer.offers.compare.sectionFit', divider: true },
  { key: 'dscr', labelKey: 'consumer.offers.compare.dscr' },
  { key: 'ltv', labelKey: 'consumer.offers.compare.ltv' },
  { key: 'deadline', labelKey: 'consumer.offers.compare.deadline' },
];

const LABEL_COL_PX = 220;
const VALUE_ROW_HEIGHT = 48;
const DIVIDER_ROW_HEIGHT = 36;

export interface OffersCompareGridProps {
  /** Active offers (only pending — accepted/rejected belong in the list view). */
  offers: OfferRecord[];
  /** ID of the winner column (gets mint gradient + Astor pick chip). */
  winnerId?: string;
  /** Accept handler (drives confirmation dialog in parent). */
  onAccept: (offer: OfferRecord) => void;
  /** Counter handler (opens counter-offer panel in parent). */
  onCounter: (offer: OfferRecord) => void;
  /** Ask Astor handler — opens a thread pre-seeded with `offer.id`. */
  onAsk: (offer: OfferRecord) => void;
}

function winnerColumnSx(theme: Theme): Parameters<typeof Box>[0]['sx'] {
  // Mint linear-gradient backdrop — subtle, derived from theme.success so it
  // honors dark/light/midnight modes without hardcoded hex.
  const tint = alpha(theme.palette.success.main, 0.08);
  const fade = alpha(theme.palette.success.main, 0.02);
  return {
    background: `linear-gradient(180deg, ${tint} 0%, ${fade} 100%)`,
    borderColor: alpha(theme.palette.success.main, 0.32),
  };
}

export function OffersCompareGrid({ offers, winnerId, onAccept, onCounter, onAsk }: OffersCompareGridProps) {
  const { t } = useI18n();
  const theme = useTheme();

  if (offers.length === 0) return null;

  // Outer container is a CSS grid: fixed label column + N flex columns.
  const gridTemplate = `${LABEL_COL_PX}px repeat(${offers.length}, minmax(220px, 1fr))`;

  return (
    <Box
      role="table"
      aria-label={t('consumer.offers.compare.title')}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Column headers — render once on top so each column is anchored */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {t('consumer.offers.compare.title')}
          </Typography>
        </Box>
        {offers.map((offer) => {
          const isWinner = offer.id === winnerId;
          return (
            <Box
              key={offer.id}
              role="columnheader"
              sx={{
                p: 1.5,
                borderLeft: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                ...(isWinner ? winnerColumnSx(theme) : {}),
              }}
            >
              {isWinner && (
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <GlassChip label={t('consumer.offers.astorPick')} size="small" color="success" variant="filled" />
                </Box>
              )}
              <OfferCellValue fieldKey="lender" offer={offer} />
            </Box>
          );
        })}
      </Box>

      {/* Comparison rows (skip lender — it's already in the header) */}
      {COMPARE_ROWS.slice(1).map((row) => (
        <Box
          key={row.key}
          role="row"
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            minHeight: row.divider ? DIVIDER_ROW_HEIGHT : VALUE_ROW_HEIGHT,
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            ...(row.divider
              ? {
                  bgcolor: (innerTheme) => alpha(innerTheme.palette.text.primary, 0.03),
                }
              : {}),
          }}
        >
          {/* Label cell */}
          <Box sx={{ px: 1.5, py: row.divider ? 0.75 : 1 }}>
            <Typography
              variant={row.divider ? 'overline' : 'body2'}
              sx={{
                color: row.divider ? 'text.secondary' : 'text.primary',
                fontWeight: row.divider ? 700 : 500,
                letterSpacing: row.divider ? '0.08em' : undefined,
              }}
            >
              {t(row.labelKey)}
            </Typography>
          </Box>
          {/* Value cells per offer */}
          {offers.map((offer) => {
            const isWinner = offer.id === winnerId;
            return (
              <Box
                key={offer.id}
                role="cell"
                sx={{
                  px: 1.5,
                  py: row.divider ? 0.75 : 1,
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  ...(isWinner ? winnerColumnSx(theme) : {}),
                }}
              >
                {!row.divider && <OfferCellValue fieldKey={row.key as CompareRowKey} offer={offer} />}
              </Box>
            );
          })}
        </Box>
      ))}

      {/* Footer action row */}
      <Box
        role="row"
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          alignItems: 'center',
          py: 1.5,
        }}
      >
        <Box />
        {offers.map((offer) => {
          const isWinner = offer.id === winnerId;
          return (
            <Box
              key={offer.id}
              sx={{
                px: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                borderLeft: '1px solid',
                borderColor: 'divider',
                ...(isWinner ? winnerColumnSx(theme) : {}),
              }}
            >
              <GlassButton
                size="small"
                variant={isWinner ? 'glow' : 'contained'}
                color={isWinner ? 'success' : 'primary'}
                fullWidth
                onClick={() => onAccept(offer)}
                sx={{ minHeight: 44 }}
              >
                {isWinner ? t('consumer.offers.acceptNamed', { lender: offer.lender }) : t('consumer.offers.accept')}
              </GlassButton>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <GlassButton
                  size="small"
                  variant="outlined"
                  onClick={() => onCounter(offer)}
                  sx={{ flex: 1, minHeight: 44 }}
                >
                  {t('consumer.offers.counter')}
                </GlassButton>
                <GlassButton
                  size="small"
                  variant="text"
                  startIcon={<ChatBubbleOutlinedIcon />}
                  onClick={() => onAsk(offer)}
                  sx={{ flex: 1, minHeight: 44 }}
                >
                  {t('consumer.offers.askAstor')}
                </GlassButton>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

OffersCompareGrid.displayName = 'OffersCompareGrid';
